import { useCallback, useEffect, useRef, useState } from "react";
import {
  ConnectionState,
  DataPacket_Kind,
  LocalParticipant,
  LocalTrackPublication,
  type LocalVideoTrack,
  Participant,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  Room,
  RoomEvent,
  Track,
} from "livekit-client";
import { ZoomFXProcessor, fxActive, type FxMode } from "../lib/fx";

import { ControlButton } from "../components/ControlButton";
import { HRDMark } from "../components/HRDMark";
import { Icon } from "../components/Icons";
import { Pill, RecDot } from "../components/Pill";
import { VideoTile } from "../components/VideoTile";
import { endMeeting, postConsent, startRecording, stopRecording } from "../api/client";
import type { MeetingPublicView } from "../api/types";
import type { PreJoinChoice } from "./PreJoin";

const ROLE_LABEL: Record<"recruiter" | "candidate", string> = {
  recruiter: "Куратор · HRDeva",
  candidate: "кандидат",
};

// In-band signaling between recruiter and candidate. LiveKit Data Channel
// carries small JSON messages; both participants subscribe and react.
type SignalingMessage =
  | { kind: "consent_request"; from: string }
  | { kind: "consent_granted"; from: string }
  | { kind: "consent_denied"; from: string }
  | { kind: "meeting_ended"; from: string };

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function InCall({
  meeting,
  roleToken,
  choice,
  onLeave,
}: {
  meeting: MeetingPublicView;
  roleToken: string | null;
  choice: PreJoinChoice;
  onLeave: () => void;
}) {
  // Room is created inside the connect effect (not via useMemo) so that React
  // StrictMode's mount → unmount → mount cycle produces a fresh Room on the
  // second mount instead of reusing the disconnected one (which throws
  // "PeerConnectionManager is closed").
  const roomRef = useRef<Room | null>(null);
  const [connState, setConnState] = useState<ConnectionState>(
    ConnectionState.Disconnected
  );
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [remoteParticipants, setRemoteParticipants] = useState<RemoteParticipant[]>([]);
  const [localVideo, setLocalVideo] = useState<Track | null>(null);
  const [remoteVideo, setRemoteVideo] = useState<RemoteTrack | null>(null);
  const [remoteMicOff, setRemoteMicOff] = useState(false);
  const [remoteCamOff, setRemoteCamOff] = useState(false);
  // Recording lifecycle is driven by the server. We use the local boolean
  // as an optimistic flag while the start/stop requests are in flight.
  const [recording, setRecording] = useState(false);
  const [recTime, setRecTime] = useState("00:00");
  const recStartRef = useRef<number | null>(null);
  const [consentGrantedAt, setConsentGrantedAt] = useState<string | null>(
    meeting.recording_consent_granted_at
  );
  const [consentRequestOpen, setConsentRequestOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  // Video FX (blur + portrait light) — initial from PreJoin, toggled mid-call.
  const [fx, setFx] = useState<FxMode>(choice.fx);
  const fxProcRef = useRef<ZoomFXProcessor | null>(null);
  const fxModeRef = useRef<FxMode>(choice.fx);
  // Device dropdowns (mid-call switch). Populated lazily when the picker opens.
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [currentVideoId, setCurrentVideoId] = useState<string | null>(
    choice.videoDeviceId
  );
  const [currentAudioId, setCurrentAudioId] = useState<string | null>(
    choice.audioDeviceId
  );
  const [picker, setPicker] = useState<"cam" | "mic" | null>(null);

  const role = meeting.role;

  // --- send / receive data messages ---------------------------------------
  const sendSignal = useCallback(async (msg: SignalingMessage) => {
    const r = roomRef.current;
    if (!r || !r.localParticipant) return;
    const bytes = encoder.encode(JSON.stringify(msg));
    await r.localParticipant.publishData(bytes, { reliable: true });
  }, []);

  const onRecordingClick = async () => {
    if (recording) {
      // Stop
      if (!roleToken) return;
      setBusy(true);
      try {
        await stopRecording(meeting.short_code, roleToken);
        setRecording(false);
        recStartRef.current = null;
      } catch (e) {
        setError(humanizeError(e, "не удалось остановить запись"));
      } finally {
        setBusy(false);
      }
      return;
    }

    if (!roleToken) return;
    if (!consentGrantedAt) {
      // Ask the candidate first.
      await sendSignal({
        kind: "consent_request",
        from: meeting.recruiter_name ?? "Рекрутёр",
      });
      return;
    }

    // Consent already granted (e.g. earlier in the call) — kick egress right away.
    setBusy(true);
    try {
      await startRecording(meeting.short_code, roleToken);
      setRecording(true);
      recStartRef.current = Date.now();
    } catch (e) {
      setError(humanizeError(e, "не удалось запустить запись"));
    } finally {
      setBusy(false);
    }
  };

  const onCandidateConsent = async (granted: boolean) => {
    setConsentRequestOpen(false);
    setBusy(true);
    try {
      const res = await postConsent(meeting.short_code, granted, { t: null });
      if (granted) {
        setConsentGrantedAt(res.recording_consent_granted_at);
        await sendSignal({ kind: "consent_granted", from: "candidate" });
      } else {
        await sendSignal({ kind: "consent_denied", from: "candidate" });
      }
    } catch (e) {
      setError(humanizeError(e, "не удалось отправить ответ"));
    } finally {
      setBusy(false);
    }
  };

  // --- room connection -----------------------------------------------------

  useEffect(() => {
    let mounted = true;
    const room = new Room({
      adaptiveStream: true,
      dynacast: true,
      videoCaptureDefaults: choice.videoDeviceId
        ? { deviceId: choice.videoDeviceId }
        : undefined,
      audioCaptureDefaults: choice.audioDeviceId
        ? { deviceId: choice.audioDeviceId }
        : undefined,
    });
    roomRef.current = room;

    const onState = (s: ConnectionState) => {
      if (!mounted) return;
      setConnState(s);
    };

    const refreshParticipants = () => {
      const arr = Array.from(room.remoteParticipants.values());
      setRemoteParticipants(arr);

      // Pick the first remote video & audio for the main tile.
      let videoTrack: RemoteTrack | null = null;
      let audioTrack: RemoteTrack | null = null;
      for (const p of arr) {
        for (const pub of p.trackPublications.values()) {
          if (!pub.track) continue;
          if (pub.kind === Track.Kind.Video && !videoTrack) {
            videoTrack = pub.track as RemoteTrack;
          }
          if (pub.kind === Track.Kind.Audio && !audioTrack) {
            audioTrack = pub.track as RemoteTrack;
          }
        }
        if (videoTrack && audioTrack) break;
      }
      setRemoteVideo(videoTrack);
      setRemoteMicOff(
        arr.length > 0 && arr.every((p) => p.isMicrophoneEnabled === false)
      );
      setRemoteCamOff(
        arr.length > 0 && arr.every((p) => p.isCameraEnabled === false)
      );

      if (remoteAudioRef.current) {
        if (audioTrack) {
          (audioTrack as unknown as {
            attach: (el: HTMLAudioElement) => void;
          }).attach(remoteAudioRef.current);
        } else {
          remoteAudioRef.current.srcObject = null;
        }
      }
    };

    const onTrackSub = (_track: RemoteTrack, _pub: RemoteTrackPublication, _p: RemoteParticipant) =>
      refreshParticipants();
    const onTrackUnsub = (_track: RemoteTrack, _pub: RemoteTrackPublication, _p: RemoteParticipant) =>
      refreshParticipants();
    const onParticipantJoin = () => refreshParticipants();
    const onParticipantLeave = () => refreshParticipants();
    const onTrackMuted = () => refreshParticipants();

    const onLocalPublished = (pub: LocalTrackPublication, _p: LocalParticipant) => {
      if (pub.kind === Track.Kind.Video && pub.track) setLocalVideo(pub.track);
    };
    const onLocalUnpublished = (pub: LocalTrackPublication) => {
      if (pub.kind === Track.Kind.Video) setLocalVideo(null);
    };

    const onData = (
      payload: Uint8Array,
      _participant?: Participant,
      _kind?: DataPacket_Kind,
    ) => {
      try {
        const msg = JSON.parse(decoder.decode(payload)) as SignalingMessage;
        switch (msg.kind) {
          case "consent_request":
            if (role === "candidate") setConsentRequestOpen(true);
            break;
          case "consent_granted":
            if (role === "recruiter") {
              setConsentGrantedAt(new Date().toISOString());
              // Now actually kick off egress.
              if (roleToken) {
                setBusy(true);
                startRecording(meeting.short_code, roleToken)
                  .then(() => {
                    setRecording(true);
                    recStartRef.current = Date.now();
                  })
                  .catch((e) =>
                    setError(humanizeError(e, "не удалось запустить запись"))
                  )
                  .finally(() => setBusy(false));
              }
            }
            break;
          case "consent_denied":
            if (role === "recruiter") {
              setError("Кандидат отказался от записи.");
            }
            break;
          case "meeting_ended":
            // Recruiter hung up — bail out of the room and route the
            // candidate to the Ended screen.
            if (role === "candidate") {
              roomRef.current?.disconnect().catch(() => undefined);
              onLeave();
            }
            break;
        }
      } catch {
        // ignore malformed data
      }
    };

    room.on(RoomEvent.ConnectionStateChanged, onState);
    room.on(RoomEvent.TrackSubscribed, onTrackSub);
    room.on(RoomEvent.TrackUnsubscribed, onTrackUnsub);
    room.on(RoomEvent.ParticipantConnected, onParticipantJoin);
    room.on(RoomEvent.ParticipantDisconnected, onParticipantLeave);
    room.on(RoomEvent.TrackMuted, onTrackMuted);
    room.on(RoomEvent.TrackUnmuted, onTrackMuted);
    room.on(RoomEvent.LocalTrackPublished, onLocalPublished);
    room.on(RoomEvent.LocalTrackUnpublished, onLocalUnpublished);
    room.on(RoomEvent.DataReceived, onData);

    (async () => {
      try {
        // meet-service ships its own iceServers (pointing at our standalone
        // coturn) because LiveKit v1.7 hard-codes the advertised `turns:`
        // port to 443, which doesn't match our deployment. By passing
        // rtcConfig.iceServers here we override LiveKit's defaults — the
        // browser uses these for both direct and relay paths.
        const connectOpts =
          meeting.ice_servers && meeting.ice_servers.length > 0
            ? {
                rtcConfig: {
                  iceServers: meeting.ice_servers.map((s) => ({
                    urls: s.urls,
                    username: s.username ?? undefined,
                    credential: s.credential ?? undefined,
                  })),
                },
              }
            : undefined;
        await room.connect(meeting.livekit_ws_url, meeting.livekit_token, connectOpts);
        if (!mounted) {
          await room.disconnect();
          return;
        }
        await room.localParticipant.setCameraEnabled(true);
        await room.localParticipant.setMicrophoneEnabled(true);
        // setCameraEnabled may resolve after the LocalTrackPublished event has
        // already fired (or the publication may pre-exist when we re-enter
        // the call), so explicitly seed local video state from current
        // publications. Without this the self-PIP stays empty.
        for (const pub of room.localParticipant.trackPublications.values()) {
          if (pub.kind === Track.Kind.Video && pub.track) {
            setLocalVideo(pub.track);
            break;
          }
        }
        // Apply PreJoin's FX preference once the video track is live.
        if (fxActive(choice.fx)) {
          for (const pub of room.localParticipant.trackPublications.values()) {
            if (pub.kind !== Track.Kind.Video || !pub.track) continue;
            try {
              const proc = new ZoomFXProcessor(choice.fx);
              fxProcRef.current = proc;
              await (pub.track as LocalVideoTrack).setProcessor(proc);
            } catch (e) {
              console.warn("initial fx failed", e);
            }
            break;
          }
        }
        // Populate device lists for the in-call picker.
        try {
          const all = await navigator.mediaDevices.enumerateDevices();
          setVideoDevices(all.filter((d) => d.kind === "videoinput"));
          setAudioDevices(all.filter((d) => d.kind === "audioinput"));
        } catch {
          // permissions may have been revoked; the picker just stays empty.
        }
        refreshParticipants();
      } catch (e) {
        if (mounted) {
          setError(humanizeError(e, "не удалось подключиться к комнате"));
        }
      }
    })();

    return () => {
      mounted = false;
      room.off(RoomEvent.ConnectionStateChanged, onState);
      room.off(RoomEvent.TrackSubscribed, onTrackSub);
      room.off(RoomEvent.TrackUnsubscribed, onTrackUnsub);
      room.off(RoomEvent.ParticipantConnected, onParticipantJoin);
      room.off(RoomEvent.ParticipantDisconnected, onParticipantLeave);
      room.off(RoomEvent.TrackMuted, onTrackMuted);
      room.off(RoomEvent.TrackUnmuted, onTrackMuted);
      room.off(RoomEvent.LocalTrackPublished, onLocalPublished);
      room.off(RoomEvent.LocalTrackUnpublished, onLocalUnpublished);
      room.off(RoomEvent.DataReceived, onData);
      room.disconnect().catch(() => undefined);
      if (roomRef.current === room) roomRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- recording timer -----------------------------------------------------
  useEffect(() => {
    if (!recording) {
      setRecTime("00:00");
      return;
    }
    const id = setInterval(() => {
      if (recStartRef.current == null) return;
      const elapsed = Math.floor((Date.now() - recStartRef.current) / 1000);
      const m = String(Math.floor(elapsed / 60)).padStart(2, "0");
      const s = String(elapsed % 60).padStart(2, "0");
      setRecTime(`${m}:${s}`);
    }, 1000);
    return () => clearInterval(id);
  }, [recording]);

  // --- toggles -------------------------------------------------------------
  const toggleMic = async () => {
    const next = !micOn;
    setMicOn(next);
    const r = roomRef.current;
    if (!r) return;
    await r.localParticipant.setMicrophoneEnabled(next).catch(() => undefined);
  };
  const toggleCam = async () => {
    const next = !camOn;
    setCamOn(next);
    const r = roomRef.current;
    if (!r) return;
    await r.localParticipant.setCameraEnabled(next).catch(() => undefined);
  };

  // --- video FX (blur + portrait light) + device switch -------------------
  // Apply / update the ZoomFX processor on the published video track. The
  // processor sits BEFORE publication, so the SFU (and the recording egress)
  // get the already-processed frames. Toggling on an attached processor is
  // instant (no wasm / worker re-init); we only stop it when both off.
  const applyFx = useCallback(async (next: FxMode) => {
    fxModeRef.current = next;
    const r = roomRef.current;
    if (!r) return;
    for (const pub of r.localParticipant.trackPublications.values()) {
      if (pub.kind !== Track.Kind.Video || !pub.track) continue;
      const track = pub.track as LocalVideoTrack;
      try {
        if (!fxActive(next)) {
          fxProcRef.current = null;
          await track.stopProcessor();
        } else if (fxProcRef.current) {
          fxProcRef.current.setMode(next);
        } else {
          const proc = new ZoomFXProcessor(next);
          fxProcRef.current = proc;
          await track.setProcessor(proc);
        }
      } catch (e) {
        console.warn("fx toggle failed", e);
      }
      break;
    }
  }, []);

  const toggleBlur = async () => {
    const next = { ...fx, blur: !fx.blur };
    setFx(next);
    await applyFx(next);
  };
  const togglePortrait = async () => {
    const next = { ...fx, portrait: !fx.portrait };
    setFx(next);
    await applyFx(next);
  };

  const refreshDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      setVideoDevices(all.filter((d) => d.kind === "videoinput"));
      setAudioDevices(all.filter((d) => d.kind === "audioinput"));
    } catch {
      // ignore — likely permissions revoked
    }
  }, []);

  const switchDevice = async (kind: "videoinput" | "audioinput", deviceId: string) => {
    const r = roomRef.current;
    if (!r) return;
    // Optimistic UI — close the picker and lock in the selected id right
    // away so the user doesn't sit on a dead dropdown while LK re-acquires
    // the device. If it fails we revert below.
    setPicker(null);
    const prevVideoId = currentVideoId;
    const prevAudioId = currentAudioId;
    if (kind === "videoinput") setCurrentVideoId(deviceId);
    else setCurrentAudioId(deviceId);

    // If FX is on, stop the processor BEFORE switching. switchActiveDevice
    // republishes the track; if the old processor was still attached, the
    // segmenter has to tear down + re-init mid-flight, which is what made
    // camera-flip take 20-30 s in practice.
    const fxMode = fxModeRef.current;
    const hadFx = kind === "videoinput" && fxActive(fxMode);
    if (hadFx) {
      fxProcRef.current = null;
      await applyFx({ blur: false, portrait: false }).catch(() => undefined);
    }

    try {
      await r.switchActiveDevice(kind, deviceId);
    } catch (e) {
      if (kind === "videoinput") setCurrentVideoId(prevVideoId);
      else setCurrentAudioId(prevAudioId);
      setError(humanizeError(e, "не удалось переключить устройство"));
      return;
    }

    if (hadFx) {
      // Fire-and-forget — re-apply FX on the new track. Don't make the UI
      // wait for MediaPipe to spin back up; the user already sees the new
      // camera stream.
      applyFx(fxMode).catch(() => undefined);
    }
  };

  // Mobile-friendly camera flip: cycle to the next videoinput.
  const flipCamera = async () => {
    const list = videoDevices.length > 0 ? videoDevices : await (async () => {
      await refreshDevices();
      const all = await navigator.mediaDevices.enumerateDevices();
      return all.filter((d) => d.kind === "videoinput");
    })();
    if (list.length < 2) {
      setError("вторая камера не найдена");
      return;
    }
    const idx = list.findIndex((d) => d.deviceId === currentVideoId);
    const next = list[(idx + 1) % list.length];
    await switchDevice("videoinput", next.deviceId);
  };

  const onEnd = async () => {
    // Recruiter "End" terminates the meeting for everyone — flip server-
    // side status to ENDED, tell the candidate via the data channel, then
    // disconnect locally. Candidate "End" just disconnects themselves.
    if (role === "recruiter" && roleToken) {
      try {
        await sendSignal({ kind: "meeting_ended", from: "recruiter" });
      } catch {
        // best-effort
      }
      try {
        await endMeeting(meeting.short_code, roleToken);
      } catch (e) {
        setError(humanizeError(e, "не удалось завершить встречу"));
      }
    }
    await roomRef.current?.disconnect().catch(() => undefined);
    onLeave();
  };

  // --- render --------------------------------------------------------------
  const mainName =
    role === "recruiter"
      ? meeting.candidate_name ?? "Кандидат"
      : meeting.recruiter_name ?? "Рекрутёр";
  const selfName =
    role === "recruiter"
      ? meeting.recruiter_name ?? "Вы"
      : meeting.candidate_name ?? "Вы";

  return (
    <div className="incall">
      <TopBar
        vacancy={meeting.vacancy_title}
        recording={recording}
        recTime={recTime}
        connState={connState}
      />

      <div className="incall-stage-wrap">
        <div className="incall-stage">
          <div className="incall-tile incall-tile-remote">
            <VideoTile
              videoTrack={remoteVideo}
              name={mainName}
              subtitle={ROLE_LABEL[role === "recruiter" ? "candidate" : "recruiter"]}
              micOff={remoteMicOff}
              cameraOff={remoteCamOff}
              placeholder={
                remoteParticipants.length === 0
                  ? role === "recruiter"
                    ? "Ждём кандидата…"
                    : "Ждём рекрутёра…"
                  : undefined
              }
              style={{ flex: 1, width: "100%", height: "100%" }}
            />
          </div>

          <div className="incall-tile incall-tile-self">
            <VideoTile
              videoTrack={localVideo}
              name={selfName}
              isLocal
              mirror
              cameraOff={!camOn}
              style={{ flex: 1, width: "100%", height: "100%" }}
            />
          </div>

          {role === "candidate" && consentRequestOpen && (
            <ConsentOverlay
              recruiterName={meeting.recruiter_name ?? "Рекрутёр"}
              onAllow={() => onCandidateConsent(true)}
              onDeny={() => onCandidateConsent(false)}
              busy={busy}
            />
          )}
        </div>

        {role === "recruiter" && (
          <RecruiterRail
            candidateName={meeting.candidate_name ?? "Кандидат"}
            vacancyTitle={meeting.vacancy_title}
            consentGranted={!!consentGrantedAt}
            recording={recording}
          />
        )}
      </div>

      {/* hidden audio sink for remote participants */}
      <audio ref={remoteAudioRef} autoPlay playsInline />

      <BottomBar
        role={role}
        recording={recording}
        micOn={micOn}
        camOn={camOn}
        blurOn={fx.blur}
        portraitOn={fx.portrait}
        canRecord={!!roleToken}
        busy={busy}
        shortCode={meeting.short_code}
        videoDevices={videoDevices}
        audioDevices={audioDevices}
        currentVideoId={currentVideoId}
        currentAudioId={currentAudioId}
        picker={picker}
        onMic={toggleMic}
        onCam={toggleCam}
        onBlur={toggleBlur}
        onPortrait={togglePortrait}
        onFlip={flipCamera}
        onOpenPicker={(k) => {
          refreshDevices();
          setPicker((cur) => (cur === k ? null : k));
        }}
        onClosePicker={() => setPicker(null)}
        onPickDevice={switchDevice}
        onRec={onRecordingClick}
        onEnd={onEnd}
      />

      {error && (
        <div
          style={{
            position: "absolute",
            top: 64,
            left: "50%",
            transform: "translateX(-50%)",
            background: "var(--red-soft)",
            color: "var(--red)",
            padding: "8px 14px",
            borderRadius: 8,
            fontSize: 12.5,
            zIndex: 80,
            border: "1px solid var(--red)",
          }}
          onClick={() => setError(null)}
        >
          {error}
        </div>
      )}
    </div>
  );
}

function TopBar({
  vacancy,
  recording,
  recTime,
  connState,
}: {
  vacancy: string | null;
  recording: boolean;
  recTime: string;
  connState: ConnectionState;
}) {
  const ok = connState === ConnectionState.Connected;
  return (
    <div className="vr-topbar">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <HRDMark size={22} />
        <span className="vr-topbar-mono">HRDeva · Видеокабинет</span>
      </div>
      <div className="vr-topbar-divider" />
      <div className="vr-topbar-meta">
        <div className="vr-topbar-title">
          {vacancy ? `Интервью · ${vacancy}` : "Видео-интервью"}
        </div>
        <div className="vr-topbar-sub">
          {connState === ConnectionState.Connecting && "подключение…"}
          {connState === ConnectionState.Connected && "защищённое соединение · WebRTC"}
          {connState === ConnectionState.Reconnecting && "переподключение…"}
          {connState === ConnectionState.Disconnected && "отключено"}
        </div>
      </div>
      {recording && (
        <Pill kind="red" mono>
          <RecDot /> ЗАПИСЬ · {recTime}
        </Pill>
      )}
      <Pill kind="glass">
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: ok ? "var(--green)" : "var(--yellow,oklch(0.82 0.13 85))",
            display: "inline-block",
          }}
        />
        {ok ? "соединение отличное" : "ожидание"}
      </Pill>
    </div>
  );
}

function BottomBar({
  role,
  recording,
  micOn,
  camOn,
  blurOn,
  portraitOn,
  canRecord,
  busy,
  shortCode,
  videoDevices,
  audioDevices,
  currentVideoId,
  currentAudioId,
  picker,
  onMic,
  onCam,
  onBlur,
  onPortrait,
  onFlip,
  onOpenPicker,
  onClosePicker,
  onPickDevice,
  onRec,
  onEnd,
}: {
  role: "recruiter" | "candidate";
  recording: boolean;
  micOn: boolean;
  camOn: boolean;
  blurOn: boolean;
  portraitOn: boolean;
  canRecord: boolean;
  busy: boolean;
  shortCode: string;
  videoDevices: MediaDeviceInfo[];
  audioDevices: MediaDeviceInfo[];
  currentVideoId: string | null;
  currentAudioId: string | null;
  picker: "cam" | "mic" | null;
  onMic: () => void;
  onCam: () => void;
  onBlur: () => void;
  onPortrait: () => void;
  onFlip: () => void;
  onOpenPicker: (kind: "cam" | "mic") => void;
  onClosePicker: () => void;
  onPickDevice: (kind: "videoinput" | "audioinput", id: string) => void;
  onRec: () => void;
  onEnd: () => void;
}) {
  const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches;
  return (
    <div className="vr-bottombar">
      <div className="vr-bottombar-link">
        <Icon name="link" size={12} />
        hrdeva.ru/v/{shortCode}
      </div>

      <ControlButtonGroup
        main={
          <ControlButton
            icon={micOn ? "mic" : "micOff"}
            active={!micOn}
            label={micOn ? "Микрофон" : "Включить"}
            onClick={onMic}
          />
        }
        chevronTitle="Выбрать микрофон"
        chevronActive={picker === "mic"}
        onChevron={!isMobile ? () => onOpenPicker("mic") : undefined}
        menu={
          picker === "mic" ? (
            <DeviceMenu
              devices={audioDevices}
              currentId={currentAudioId}
              onPick={(id) => onPickDevice("audioinput", id)}
              onClose={onClosePicker}
            />
          ) : null
        }
      />
      <ControlButtonGroup
        main={
          <ControlButton
            icon={camOn ? "cam" : "camOff"}
            active={!camOn}
            label="Камера"
            onClick={onCam}
          />
        }
        chevronTitle={isMobile ? "Сменить камеру" : "Выбрать камеру"}
        chevronActive={picker === "cam"}
        // On mobile the chevron flips front/back directly (no dropdown).
        onChevron={isMobile ? onFlip : () => onOpenPicker("cam")}
        chevronIcon={isMobile ? "camFlip" : undefined}
        menu={
          picker === "cam" ? (
            <DeviceMenu
              devices={videoDevices}
              currentId={currentVideoId}
              onPick={(id) => onPickDevice("videoinput", id)}
              onClose={onClosePicker}
            />
          ) : null
        }
      />
      <ControlButton
        icon="blur"
        active={blurOn}
        label={blurOn ? "Без фона" : "Размыть фон"}
        onClick={onBlur}
      />
      <ControlButton
        icon="sun"
        active={portraitOn}
        label={portraitOn ? "Без света" : "Портретный свет"}
        onClick={onPortrait}
      />

      {role === "recruiter" && (
        <>
          <div className="vr-divider" />
          {recording ? (
            <ControlButton
              icon="stop"
              danger
              label={busy ? "…" : "Остановить"}
              onClick={onRec}
              disabled={busy}
            />
          ) : (
            <ControlButton
              icon="rec"
              size="lg"
              danger
              label={busy ? "…" : "Запись"}
              onClick={onRec}
              disabled={busy || !canRecord}
            />
          )}
        </>
      )}

      <div className="vr-divider" />
      <button className="vr-end" onClick={onEnd}>
        <Icon name="end" size={18} stroke={2.2} />
        {role === "recruiter" ? "Завершить" : "Покинуть"}
      </button>
    </div>
  );
}

function ControlButtonGroup({
  main,
  chevronTitle,
  chevronActive,
  chevronIcon,
  onChevron,
  menu,
}: {
  main: React.ReactNode;
  chevronTitle: string;
  chevronActive?: boolean;
  chevronIcon?: string;
  onChevron?: () => void;
  menu: React.ReactNode;
}) {
  return (
    <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 4 }}>
      {main}
      {onChevron && (
        <button
          onClick={onChevron}
          title={chevronTitle}
          aria-label={chevronTitle}
          style={{
            background: chevronActive ? "var(--panel-2)" : "transparent",
            color: "var(--text-2)",
            border: 0,
            borderRadius: 8,
            width: 22,
            height: 32,
            display: "grid",
            placeItems: "center",
            cursor: "pointer",
          }}
        >
          <Icon name={chevronIcon ?? "more"} size={14} />
        </button>
      )}
      {menu}
    </div>
  );
}

function DeviceMenu({
  devices,
  currentId,
  onPick,
  onClose,
}: {
  devices: MediaDeviceInfo[];
  currentId: string | null;
  onPick: (deviceId: string) => void;
  onClose: () => void;
}) {
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "transparent",
          zIndex: 30,
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "calc(100% + 8px)",
          left: 0,
          minWidth: 240,
          maxWidth: 320,
          background: "var(--panel-2)",
          border: "1px solid var(--border-faint)",
          borderRadius: 10,
          padding: 4,
          boxShadow: "0 12px 32px oklch(0 0 0 / 0.5)",
          zIndex: 40,
        }}
      >
        {devices.length === 0 && (
          <div style={{ padding: "10px 12px", fontSize: 12, color: "var(--text-3)" }}>
            устройств не найдено
          </div>
        )}
        {devices.map((d) => (
          <button
            key={d.deviceId}
            onClick={() => onPick(d.deviceId)}
            style={{
              width: "100%",
              textAlign: "left",
              background:
                d.deviceId === currentId ? "oklch(0.28 0.05 155)" : "transparent",
              color: "var(--text)",
              border: 0,
              borderRadius: 8,
              padding: "8px 10px",
              fontSize: 12.5,
              cursor: "pointer",
            }}
          >
            {d.label || "(без названия)"}
          </button>
        ))}
      </div>
    </>
  );
}

function RecruiterRail({
  candidateName,
  vacancyTitle,
  consentGranted,
  recording,
}: {
  candidateName: string;
  vacancyTitle: string | null;
  consentGranted: boolean;
  recording: boolean;
}) {
  return (
    <aside className="incall-rail">
      <div className="rail-header">
        <HRDMark size={28} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 500 }}>{candidateName}</div>
          <div
            style={{
              fontSize: 11,
              color: "var(--text-3)",
              fontFamily: "var(--font-mono)",
              marginTop: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {vacancyTitle ?? "видео-интервью"}
          </div>
        </div>
      </div>

      <div className="rail-section">
        <div className="rail-section-title">состояние</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <StatusRow label="Согласие на запись" ok={consentGranted} />
          <StatusRow label="Идёт запись" ok={recording} ghost={!recording} />
        </div>
      </div>

      <div className="rail-section" style={{ flex: 1 }}>
        <div className="rail-section-title">заметки куратора</div>
        <textarea
          placeholder="→ ключевые моменты разговора"
          style={{
            width: "100%",
            minHeight: 140,
            resize: "vertical",
            background: "var(--panel-2)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            padding: "8px 10px",
            fontSize: 12.5,
            color: "var(--text)",
            fontFamily: "var(--font-sans)",
            lineHeight: 1.5,
          }}
        />
        <div
          style={{
            fontSize: 10.5,
            color: "var(--text-4)",
            marginTop: 6,
            fontFamily: "var(--font-mono)",
          }}
        >
          сохраняются только локально в MVP
        </div>
      </div>
    </aside>
  );
}

function StatusRow({ label, ok, ghost }: { label: string; ok: boolean; ghost?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span
        style={{
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: ghost ? "transparent" : ok ? "var(--green)" : "var(--text-4)",
          border: ghost ? "1.5px solid var(--border)" : "none",
          color: "var(--accent-fg)",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        {ok && !ghost && <Icon name="check" size={10} stroke={3} />}
      </span>
      <span style={{ fontSize: 12.5, color: ok ? "var(--text)" : "var(--text-2)" }}>
        {label}
      </span>
    </div>
  );
}

function ConsentOverlay({
  recruiterName,
  onAllow,
  onDeny,
  busy,
}: {
  recruiterName: string;
  onAllow: () => void;
  onDeny: () => void;
  busy: boolean;
}) {
  return (
    <div className="consent-backdrop">
      <div className="consent-card">
        <div className="consent-rec-icon">
          <Icon name="rec" size={22} stroke={2} />
        </div>
        <div
          style={{
            fontSize: 19,
            fontWeight: 600,
            letterSpacing: "-0.01em",
            marginBottom: 8,
          }}
        >
          {recruiterName} хочет начать запись
        </div>
        <div
          style={{
            fontSize: 13,
            color: "var(--text-2)",
            lineHeight: 1.55,
            marginBottom: 14,
          }}
        >
          Запись будет использоваться для составления карточки кандидата и не
          передаётся третьим сторонам. Хранится в защищённом виде в системе
          HRDeva.
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: "var(--text-3)",
            padding: 10,
            background: "var(--panel-2)",
            borderRadius: 6,
            marginBottom: 4,
          }}
        >
          Вы можете остановить запись в любой момент кнопкой в углу экрана.
        </div>
        <div className="consent-actions">
          <button
            className="consent-btn-primary"
            disabled={busy}
            onClick={onAllow}
          >
            {busy ? "…" : "Разрешить запись"}
          </button>
          <button
            className="consent-btn-ghost"
            disabled={busy}
            onClick={onDeny}
          >
            Отказать
          </button>
        </div>
      </div>
    </div>
  );
}

function humanizeError(e: unknown, fallback: string): string {
  if (e instanceof Error) return e.message || fallback;
  return fallback;
}
